import { Control, FieldValues, Path } from 'react-hook-form';
import { z } from 'zod';

import { FormAddEmail } from './FormAddEmail';
import { FormAutocomplete } from './FormAutocomplete';
import { FormCheckbox } from './FormCheckbox';
import { FormColorPicker } from './FormColor';
import { FormDate } from './FormDate';
import { FormDays } from './FormDays';
import { FormImage } from './FormImage';
import { FormInput } from './FormInput';
import { FormInputHex } from './FormInputHex';
import { FormNumberInput } from './FormNumberInput';
import { FormPhone } from './FormPhone';
import { FormSelect } from './FormSelect';
import { FormSelectInfoscreen } from './FormSelectInfoscreen';
import { FormSelectMany } from './FormSelectMany';
import { FormSelectMultipleTags } from './FormSelectMultipleTags';
import { FormSelectOne } from './FormSelectOne';
import { FormSelectUser } from './FormSelectUser';
import { FormSlider } from './FormSlider';
import { FormSocialLink } from './FormSocialLink';
import { FormSwitch } from './FormSwitch';

interface InputOption {
    color?: string;
    description?: string | null;
    id?: string | number | null;
    label?: string;
    name?: string;
    value?: string | number | null;
}

export interface BaseInputProps<T extends FieldValues> {
    buttonText?: string;
    control: Control<T>;
    dependentField?: Path<T>;
    dialogType?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    helperText?: string;
    iconType?: string;
    imageHeight?: number;
    imageWidth?: number;
    input?: string;
    label?: string;
    maxHeight?: number;
    maxWidth?: number;
    multiline?: boolean;
    options?: InputOption[];
    required?: boolean;
    small?: boolean;
    type?: string;
    validation?: z.ZodTypeAny;
    value: Path<T>;
    withoutEnlargement?: boolean;
}

export default function GetFormInput<T extends FieldValues>({
    buttonText,
    control,
    dependentField,
    disabled,
    fullWidth,
    helperText,
    iconType,
    imageHeight,
    imageWidth,
    label,
    maxHeight,
    maxWidth,
    multiline,
    options,
    required,
    small,
    type,
    validation,
    value,
    withoutEnlargement,
}: Readonly<BaseInputProps<T>>) {
    switch (type) {
        case 'string':
            return (
                <FormInput
                    control={control}
                    dependentField={dependentField}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    multiline={multiline}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        case 'number':
            return (
                <FormNumberInput
                    control={control}
                    dependentField={dependentField}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        case 'selectOne':
            return (
                <FormSelectOne
                    control={control}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    options={options}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        case 'selectMany':
            return (
                <FormSelectMany
                    control={control}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    options={options}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        case 'selectMultipleTags':
            return (
                <FormSelectMultipleTags
                    control={control}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    options={options}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        case 'select':
            return (
                <FormSelect
                    control={control}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    required={required}
                    validation={validation}
                    value={value}
                />
            );
        case 'selectUser':
            return (
                <FormSelectUser
                    control={control}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    required={required}
                    validation={validation}
                    value={value}
                />
            );
        case 'selectInfoscreen':
            return (
                <FormSelectInfoscreen
                    control={control}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    required={required}
                    validation={validation}
                    value={value}
                />
            );
        case 'autocomplete':
            return (
                <FormAutocomplete
                    control={control}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    options={options}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        case 'boolean':
            return (
                <FormSwitch
                    control={control}
                    disabled={disabled}
                    helperText={helperText}
                    label={label}
                    required={required}
                    validation={validation}
                    value={value}
                />
            );
        case 'checkbox':
            return (
                <FormCheckbox
                    control={control}
                    disabled={disabled}
                    helperText={helperText}
                    label={label}
                    required={required}
                    validation={validation}
                    value={value}
                />
            );
        case 'hex':
            return (
                <FormInputHex
                    control={control}
                    dependentField={dependentField}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        case 'date':
            return (
                <FormDate control={control} helperText={helperText} label={label} required={required} value={value} />
            );
        case 'days':
            return (
                <FormDays control={control} helperText={helperText} label={label} required={required} value={value} />
            );
        case 'image':
            return (
                <FormImage
                    buttonText={buttonText}
                    control={control}
                    helperText={helperText}
                    imageHeight={imageHeight}
                    imageWidth={imageWidth}
                    label={label}
                    maxHeight={maxHeight}
                    maxWidth={maxWidth}
                    required={required}
                    value={value}
                    withoutEnlargement={withoutEnlargement}
                />
            );
        case 'color':
            return (
                <FormColorPicker
                    control={control}
                    helperText={helperText}
                    label={label}
                    required={required}
                    value={value}
                />
            );
        case 'slider':
            return (
                <FormSlider control={control} helperText={helperText} label={label} required={required} value={value} />
            );
        case 'socialLink':
            return (
                <FormSocialLink
                    control={control}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    iconType={iconType}
                    label={label}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        case 'phone':
            return (
                <FormPhone
                    control={control}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    iconType={iconType}
                    label={label}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        case 'addEmail':
            return (
                <FormAddEmail
                    control={control}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    iconType={iconType}
                    label={label}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
        default:
            return (
                <FormInput
                    control={control}
                    dependentField={dependentField}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    multiline={multiline}
                    required={required}
                    small={small}
                    validation={validation}
                    value={value}
                />
            );
    }
}
